import p5 from "p5";

export class App {
  private p?: any;
  private container: HTMLElement | null = null;

  initialize(): void {
    this.container = document.getElementById("p5-container");
    if (!this.container) {
      throw new Error("#p5-container が見つかりません");
    }

    ensureAntonStylesheet();

    const sketch = (s: any) => {
      let offset = 0;
      let waveGraphics: any;
      let maskGraphics: any;
      let targetHue = 205;
      let currentHue = 205;
      let antonReady = false;
      // スクロール出現アニメーション用
      let isInView = false;
      let revealStartMs = 0;
      let revealProgress = 0; // 0..1
      const revealDurationMs = 1000;
      // ステージ: -1=未開始, 0=文字タイプ出現, 1=波スライドイン, 2=完了
      let revealStage = -1;
      let typingStartMs = 0;
      const typingCharMs = 80;
      const TITLE_TEXT = "MOVING WAVE";

      type TrailPoint = { x: number; y: number; life: number; hue: number };
      let mouseTrail: TrailPoint[] = [];

      class Particle {
        x: number;
        y: number;
        vx: number;
        vy: number;
        life: number;
        size: number;
        hue: number;
        rotation: number;
        rotSpeed: number;
        constructor(x: number, y: number, hue: number) {
          this.x = x;
          this.y = y;
          this.vx = s.random(-3, 3);
          this.vy = s.random(-5, -1);
          this.life = 255;
          this.size = s.random(3, 10);
          this.hue = hue;
          this.rotation = s.random(s.TWO_PI);
          this.rotSpeed = s.random(-0.1, 0.1);
        }
        update(): boolean {
          this.x += this.vx;
          this.y += this.vy;
          this.vy += 0.15;
          this.life -= 4;
          this.rotation += this.rotSpeed;
          return this.life > 0;
        }
        display(g: any): void {
          g.push();
          g.translate(this.x, this.y);
          g.rotate(this.rotation);
          g.noStroke();
          g.fill(this.hue, 70, 100, this.life);
          drawStar(g, 0, 0, this.size / 2, this.size, 5);
          g.pop();
        }
      }

      class Ripple {
        x: number;
        y: number;
        radius: number;
        maxRadius: number;
        alpha: number;
        speed: number;
        hue: number;
        innerRipples: { delay: number; radius: number }[];
        constructor(x: number, y: number, hue: number) {
          this.x = x;
          this.y = y;
          this.radius = 0;
          this.maxRadius = 350;
          this.alpha = 255;
          this.speed = 6;
          this.hue = hue;
          this.innerRipples = [];
          for (let i = 0; i < 3; i++) this.innerRipples.push({ delay: i * 10, radius: 0 });
        }
        update(): boolean {
          this.radius += this.speed;
          this.alpha = s.map(this.radius, 0, this.maxRadius, 255, 0);
          for (const inner of this.innerRipples) {
            if (this.radius > inner.delay) inner.radius = this.radius - inner.delay;
          }
          return this.radius < this.maxRadius;
        }
        getInfluence(px: number, py: number): number {
          let total = 0;
          const d = s.dist(px, py, this.x, this.y);
          if (d < this.radius && d > this.radius - 60) {
            total += s.sin(s.map(d, this.radius - 60, this.radius, 0, s.PI)) * (this.alpha / 255);
          }
          for (const inner of this.innerRipples) {
            if (inner.radius > 0) {
              if (d < inner.radius && d > inner.radius - 40) {
                total +=
                  s.sin(s.map(d, inner.radius - 40, inner.radius, 0, s.PI)) *
                  (this.alpha / 255) *
                  0.5;
              }
            }
          }
          return total;
        }
        display(g: any): void {
          g.stroke(this.hue, 50, 100, this.alpha * 0.6);
          g.strokeWeight(3);
          g.noFill();
          g.ellipse(this.x, this.y, this.radius * 2);
          for (const inner of this.innerRipples) {
            if (inner.radius > 0) {
              const a = s.map(inner.radius, 0, this.maxRadius, 255, 0);
              g.stroke(this.hue, 30, 100, a * 0.3);
              g.strokeWeight(2);
              g.ellipse(this.x, this.y, inner.radius * 2);
            }
          }
        }
      }

      let particles: Particle[] = [];
      let ripples: Ripple[] = [];

      s.setup = () => {
        const cnv = s.createCanvas(s.windowWidth, s.windowHeight);
        cnv.parent(this.container!);
        s.pixelDensity(1);
        s.noSmooth();
        // 画像スムージング無効化（ぼやけ軽減）
        (s.drawingContext as any).imageSmoothingEnabled = false;

        waveGraphics = s.createGraphics(s.width, s.height);
        maskGraphics = s.createGraphics(s.width, s.height);
        (waveGraphics.drawingContext as any).imageSmoothingEnabled = false;
        (maskGraphics.drawingContext as any).imageSmoothingEnabled = false;

        drawMaskText(maskGraphics, "MOVING WAVE", antonReady);

        s.colorMode(s.HSB, 360, 100, 100, 255);
        waveGraphics.colorMode(s.HSB, 360, 100, 100, 255);
        s.textAlign(s.CENTER, s.CENTER);

        if ((document as any).fonts && (document as any).fonts.load) {
          (document as any).fonts
            .load('150px "Anton"')
            .then(() => {
              antonReady = true;
              drawMaskText(maskGraphics, "MOVING WAVE", antonReady);
            })
            .catch(() => {});
        }

        // IntersectionObserver で可視範囲に入ったら出現アニメ開始
        const observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.target === this.container) {
                if (entry.isIntersecting) {
                  isInView = true;
                  // ステージ0から開始（タイプ表示）
                  revealStage = 0;
                  typingStartMs = s.millis();
                  revealProgress = 0;
                } else {
                  isInView = false;
                  // 離れたらリセット（再入場で再生）
                  revealStage = -1;
                  revealProgress = 0;
                }
              }
            }
          },
          { threshold: 0.5 }
        );
        observer.observe(this.container!);
      };

      s.mousePressed = () => {
        ripples.push(new Ripple(s.mouseX, s.mouseY, currentHue));
        // クリック時の星パーティクルは生成しない
      };

      s.mouseMoved = () => {
        // 赤(≈340) → 緑(≈100) を跨いで周回させる。負値/超過は 0..360 に折り返す
        const rawHue = s.map(s.mouseX, 0, s.width, 340, 100);
        targetHue = ((rawHue % 360) + 360) % 360;
        mouseTrail.push({ x: s.mouseX, y: s.mouseY, life: 100, hue: targetHue });
        if (mouseTrail.length > 25) mouseTrail.shift();
      };

      s.draw = () => {
        s.colorMode(s.RGB, 255);
        s.background(217, 255, 79);

        // 追従を速くして変化を分かりやすく
        currentHue = s.lerp(currentHue, targetHue, 0.2);

        waveGraphics.clear();
        waveGraphics.colorMode(s.HSB, 360, 100, 100, 255);
        waveGraphics.noStroke();

        mouseTrail = mouseTrail.map((p) => ({ ...p, life: p.life - 2 })).filter((p) => p.life > 0);
        ripples = ripples.filter((r) => r.update());

        // 出現アニメーション: ステージに応じて進行
        const initialShift = s.height * 0.35; // クリッピング範囲より十分下から
        if (revealStage === 1) {
          const t = s.constrain((s.millis() - revealStartMs) / revealDurationMs, 0, 1);
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          revealProgress = eased;
          if (t >= 1) revealStage = 2;
        } else if (revealStage <= 0) {
          revealProgress = 0;
        }
        const yShift = (1 - revealProgress) * initialShift;

        waveGraphics.push();
        waveGraphics.translate(0, yShift);

        for (let layer = 2; layer >= 0; layer--) {
          const alpha = s.map(layer, 0, 2, 255, 120);
          const saturation = s.map(layer, 0, 2, 85, 70); // 彩度は常に高め
          const brightness = s.map(layer, 0, 2, 98, 90); // 明度を落としすぎない
          waveGraphics.fill(currentHue, saturation, brightness, alpha);
          waveGraphics.beginShape();
          waveGraphics.vertex(0, s.height);
          for (let x = 0; x <= s.width; x += 2) {
            let y = s.height * 0.5;
            let trailEffect = 0;
            for (const point of mouseTrail) {
              const d = s.dist(x, y, point.x, point.y);
              if (d < 100) trailEffect += (1 - d / 100) * 8 * (point.life / 100);
            }
            let rippleEffect = 0;
            for (const rp of ripples) rippleEffect += rp.getInfluence(x, y) * 60;
            const layerOffset = layer * s.PI * 0.25;
            y += s.sin(x * 0.008 + offset + layerOffset) * (50 - layer * 15);
            y += s.sin(x * 0.015 + offset * 1.3 + layerOffset) * (25 - layer * 5);
            y -= trailEffect;
            y -= rippleEffect * (1 - layer * 0.2);
            waveGraphics.vertex(x, y);
          }
          waveGraphics.vertex(s.width, s.height);
          waveGraphics.endShape(s.CLOSE);
        }

        waveGraphics.beginShape();
        waveGraphics.vertex(0, s.height);
        for (let x = 0; x <= s.width; x += 2) {
          let y = s.height * 0.5;
          const rippleLocal = ripples.reduce((acc, rp) => acc + rp.getInfluence(x, y) * 80, 0);
          let mouseEffect = 0;
          const d = s.dist(x, y, s.mouseX, s.mouseY);
          if (d < 150) mouseEffect = (1 - d / 150) * 25;
          y += s.sin(x * 0.01 + offset) * 45;
          y += s.sin(x * 0.02 + offset * 1.5) * 20;
          y -= mouseEffect;
          y -= rippleLocal;
          waveGraphics.vertex(x, y);
        }
        waveGraphics.vertex(s.width, s.height);
        waveGraphics.endShape(s.CLOSE);

        for (const rp of ripples) rp.display(waveGraphics);

        particles = particles.filter((p) => p.update());
        for (const ptc of particles) ptc.display(waveGraphics);

        waveGraphics.colorMode(s.RGB);
        for (let i = 0; i < 8; i++) {
          const sparkleX = ((offset * 40 + i * 100) % (s.width + 100)) - 50;
          const sparkleY = s.height * 0.5 + s.sin(offset * 1.5 + i) * 70;
          const sparkleSize = 3 + s.sin(offset * 3 + i) * 2;
          const sparkleAlpha = 150 + s.sin(offset * 2 + i) * 100;
          waveGraphics.fill(255, 255, 255, sparkleAlpha);
          waveGraphics.noStroke();
          waveGraphics.push();
          waveGraphics.translate(sparkleX, sparkleY);
          waveGraphics.rotate(offset + i);
          drawStar(waveGraphics, 0, 0, sparkleSize / 2, sparkleSize, 6);
          waveGraphics.pop();
        }

        waveGraphics.fill(255, 255, 255, 180);
        for (let i = 0; i < 15; i++) {
          const bubbleX = ((offset * 60 + i * 60) % (s.width + 100)) - 50;
          const bubbleY = s.height * 0.5 + s.sin(offset * 1.5 + i * 0.5) * 40;
          let size = 4 + s.sin(offset * 2 + i) * 3;
          for (const rp of ripples) {
            const d2 = s.dist(bubbleX, bubbleY, rp.x, rp.y);
            if (d2 < rp.radius) size += (1 - d2 / rp.radius) * 8 * (rp.alpha / 255);
          }
          waveGraphics.ellipse(bubbleX, bubbleY, size, size);
        }

        waveGraphics.pop();

        const maskedWave = waveGraphics.get();
        const maskImg = maskGraphics.get();
        (maskedWave as any).mask(maskImg as any);
        s.colorMode(s.RGB);
        s.image(maskedWave, 0, 0);

        // 情報テキスト（マスクには含めず、画面に直接表示）
        s.push();
        // メイン文字を画面に収めるサイズにフィット
        const mainFont = antonReady ? "Anton" : "sans-serif";
        const fittedMainSize = computeFittedTextSize(
          TITLE_TEXT,
          s.width * 0.9,
          s.height * 0.8,
          antonReady
        );
        s.textFont(mainFont);
        s.textSize(fittedMainSize);
        const mainW = s.textWidth("MOVING WAVE");
        const mainH = s.textAscent() + s.textDescent();
        const leftX = s.width / 2 - mainW / 2;
        const topY = s.height / 2 - mainH / 2;

        // 補助テキストは画面に合わせてフィット
        s.textAlign(s.LEFT, s.TOP);
        s.textFont("Anton");
        s.noStroke();
        s.fill(0);
        const helperText = "CLICK OR HOVER OVER IT.";
        const helperSizeRaw =
          computeFittedTextSize(helperText, s.width * 0.92, mainH * 0.8, true) || 16;
        const helperSize = Math.min(helperSizeRaw, 30);
        s.textSize(helperSize);
        const helperGap = s.width >= 789 ? 5 : 30;
        const helperH = s.textAscent() + s.textDescent();
        const helperY = topY - helperGap - helperH;
        s.text(helperText, leftX, helperY);
        s.pop();

        s.colorMode(s.HSB, 360, 100, 100, 255);
        s.noFill();
        // 枠線を黒に変更（HSBでS=0,B=0 => 黒）
        s.stroke(0, 0, 0, 150);
        s.strokeWeight(3);
        s.textFont(antonReady ? "Anton" : "sans-serif");
        const mainTextSizePx2 = computeFittedTextSize(
          TITLE_TEXT,
          s.width * 0.9,
          s.height * 0.8,
          antonReady
        );
        s.textSize(mainTextSizePx2);
        s.textStyle(s.NORMAL);
        // ぼけを抑えるため整数座標にスナップ
        const cx = Math.round(s.width / 2);
        const cy = Math.round(s.height / 2);
        // タイプ出現（ステージ0）
        let drawTitle = revealStage < 0 ? "" : TITLE_TEXT;
        if (revealStage === 0) {
          const dt = s.millis() - typingStartMs;
          const shown = s.constrain(Math.floor(dt / typingCharMs), 0, TITLE_TEXT.length);
          drawTitle = TITLE_TEXT.substring(0, shown);
          if (shown >= TITLE_TEXT.length) {
            revealStage = 1;
            revealStartMs = s.millis();
          }
        }
        if (drawTitle.length > 0) {
          s.text(drawTitle, cx, cy);
        }

        offset += 0.035;
      };

      s.windowResized = () => {
        s.resizeCanvas(s.windowWidth, s.windowHeight);
        waveGraphics = s.createGraphics(s.width, s.height);
        maskGraphics = s.createGraphics(s.width, s.height);
        (waveGraphics.drawingContext as any).imageSmoothingEnabled = false;
        (maskGraphics.drawingContext as any).imageSmoothingEnabled = false;
        drawMaskText(maskGraphics, "MOVING WAVE", antonReady);
      };

      function drawMaskText(g: any, word: string, useAnton: boolean) {
        g.clear();
        g.fill(255);
        g.noStroke();
        g.textAlign(s.CENTER, s.CENTER);
        const fitted = computeFittedTextSize(word, s.width * 0.9, s.height * 0.8, useAnton);
        g.textSize(fitted);
        g.textFont(useAnton ? "Anton" : "sans-serif");
        g.text(word, s.width / 2, s.height / 2);
      }

      function drawStar(g: any, x: number, y: number, r1: number, r2: number, n: number) {
        const angle = s.TWO_PI / n;
        const half = angle / 2.0;
        g.beginShape();
        for (let a = -s.PI / 2; a < s.TWO_PI - s.PI / 2; a += angle) {
          let sx = x + s.cos(a) * r2;
          let sy = y + s.sin(a) * r2;
          g.vertex(sx, sy);
          sx = x + s.cos(a + half) * r1;
          sy = y + s.sin(a + half) * r1;
          g.vertex(sx, sy);
        }
        g.endShape(s.CLOSE);
      }

      // テキストがはみ出さないように収まる最大サイズを算出
      function computeFittedTextSize(
        word: string,
        maxW: number,
        maxH: number,
        useAnton: boolean
      ): number {
        s.push();
        s.textFont(useAnton ? "Anton" : "sans-serif");
        let base = Math.max(8, Math.min(s.width, s.height) * 0.22);
        s.textSize(base);
        const w = Math.max(1, s.textWidth(word));
        const h = Math.max(1, s.textAscent() + s.textDescent());
        const scale = Math.min(maxW / w, maxH / h, 1);
        const fitted = Math.max(8, base * scale);
        s.pop();
        return fitted;
      }

      if ((document as any).fonts && (document as any).fonts.load) {
        (document as any).fonts.load('150px "Anton"').then(() => {
          antonReady = true;
          if (maskGraphics) drawMaskText(maskGraphics, "MOVING WAVE", antonReady);
        });
      }
    };

    this.p = new p5(sketch);
  }

  destroy(): void {
    if (this.p) {
      this.p.remove();
      this.p = undefined;
    }
  }
}

function ensureAntonStylesheet() {
  const id = "gf-anton";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Anton&display=swap";
  document.head.appendChild(link);
}
